/**
 * Get currency icon based on currency code (local asset fallback).
 * @param currency - Currency code (e.g., 'BTC', 'ETH', 'USDT')
 * @returns Image source for the currency icon
 */
const getLocalCurrencyIcon = (currency: string): any | null => {
  const currencyLower = currency.toLowerCase();
  switch (currencyLower) {
    case 'btc':
    case 'bitcoin':
      return require('../assets/currencies/btc.png');
    case 'eth':
    case 'ethereum':
      return require('../assets/currencies/eth.png');
    case 'usdt':
    case 'tether':
      return require('../assets/currencies/usdt.png');
    case 'usdc':
      return require('../assets/currencies/usdc.png');
    case 'usd':
    case 'dollar':
      return require('../assets/currencies/usd.png');
    case 'eur':
    case 'euro':
      return require('../assets/currencies/eur.png');
    case 'sol':
    case 'solana':
      return require('../assets/currencies/sol.png');
    case 'bnb':
      return require('../assets/currencies/bnb.png');
    case 'ltc':
    case 'litecoin':
      return require('../assets/currencies/ltc.png');
    case 'link':
    case 'chainlink':
      return require('../assets/currencies/link.png');
    case 'avax':
    case 'avalanche':
      return require('../assets/currencies/avax.png');
    case 'xrp':
    case 'ripple':
      return require('../assets/currencies/xrp.png');
    case 'ada':
    case 'cardano':
      return require('../assets/currencies/ada.png');
    case 'doge':
    case 'dogecoin':
      return require('../assets/currencies/doge.png');
    case 'ton':
    case 'toncoin':
      return require('../assets/currencies/ton.png');
    case 'tron':
    case 'trx':
      return require('../assets/currencies/usdt.png'); // TRON typically used for USDT transfers
    default:
      return null;
  }
};

export const getCurrencyIcon = (currency: string): any =>
  getLocalCurrencyIcon(currency) ?? require('../assets/currencies/usdt.png');

/**
 * Bundled icon for the symbol — what to fall back to when an icon URL does
 * not load. Always returns a source (USDT stands in for unknown symbols).
 */
export const getLocalTokenIcon = (symbol: string): any => getCurrencyIcon(symbol);

/**
 * Get token icon source. The catalogue icon wins: it is network-specific
 * (USDT on Tron and on Ethereum carry their chain badge), which a bundled
 * per-symbol asset cannot express. Bundled assets cover tokens without a
 * URL — and TokenIcon falls back to them when a URL fails to load.
 *
 * Usage with <Image>:
 *   <Image source={getTokenIconSource('BTC', balance.icon_url)} />
 *
 * @param symbol   - Token symbol (e.g. 'BTC')
 * @param iconUrl  - Optional icon URL returned from the v2 API
 * @returns Image source (either { uri } or a local require())
 */
export const getTokenIconSource = (
  symbol: string,
  iconUrl?: string | null,
): any => {
  if (iconUrl) {
    return { uri: iconUrl };
  }
  return getCurrencyIcon(symbol);
};
